import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { getUserDecks, Deck } from '../services/deckService';
import { createClass } from '../services/classService';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Stack,
  OutlinedInput,
  Alert,
  Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

const CreateClassPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [decks, setDecks] = useState<Deck[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    selectedDecks: [] as string[],
  });

  useEffect(() => {
    const fetchDecks = async () => {
      if (user) {
        const userDecks = await getUserDecks(user.uid);
        setDecks(userDecks);
        setIsLoading(false);
      }
    };

    fetchDecks();
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      setError(t('classes.mustBeLoggedIn'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const classId = await createClass(
        formData.name,
        formData.description,
        user.uid,
        formData.selectedDecks
      );
      navigate(`/classes/${classId}`);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('common.unexpectedError'));
      }
      console.error('Error creating class:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    navigate('/signin');
    return null;
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{ mb: 2 }}
          >
            {t('common.back')}
          </Button>
          <Typography variant="h4" gutterBottom>
            {t('classes.createClass')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('classes.fillClassInfo')}
          </Typography>
        </Box>

        <Paper sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                label={t('classes.className')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
                helperText={t('classes.chooseClassName')}
              />

              <TextField
                label={t('classes.description')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                fullWidth
                helperText={t('classes.classDescription')}
              />

              <FormControl fullWidth>
                <InputLabel 
                  id="decks-select-label"
                  sx={{
                    '&.Mui-focused': {
                      color: 'primary.main',
                    },
                  }}
                >
                  {t('classes.availableDecks')}
                </InputLabel>
                <Select
                  labelId="decks-select-label"
                  multiple
                  value={formData.selectedDecks}
                  onChange={(e) => setFormData({ ...formData, selectedDecks: e.target.value as string[] })}
                  input={<OutlinedInput label={t('classes.availableDecks')} />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((deckId) => {
                        const deck = decks.find(d => d.id === deckId);
                        return (
                          <Chip
                            key={deckId}
                            label={deck?.title || deckId}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {decks.map((deck) => (
                    <MenuItem key={deck.id} value={deck.id}>
                      {deck.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/')}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={!formData.name || formData.selectedDecks.length === 0 || isSubmitting}
                >
                  {isSubmitting ? t('classes.creating') : t('classes.createClass')}
                </Button>
              </Box>
            </Stack>
          </form>
        </Paper>

        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert onClose={() => setError(null)} severity="error">
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default CreateClassPage; 